'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { 
  LayoutDashboard, 
  Wallet, 
  LayoutGrid, 
  TrendingUp, 
  Settings, 
  Users, 
  ChevronRight,
  CreditCard,
  Flag,
  Menu,
  X,
  Lightbulb,
  Repeat
} from 'lucide-react';
import { clsx } from 'clsx';
import { useSidebar } from './SidebarProvider';
import { getSession, logout, getCurrentUser } from '@/app/actions/auth';
import ConfirmModal from './ConfirmModal';

const menuItems = [
// ... previous menu items ...
  { icon: LayoutDashboard, name: 'Dashboard', path: '/' },
  { icon: Wallet, name: 'Transactions', path: '/transactions' },
  { icon: LayoutGrid, name: 'Budget', path: '/budget' },
  { icon: Repeat, name: 'Recurring Costs', path: '/fixed-costs' },
  { icon: Flag, name: 'Savers', path: '/goals' },
  { icon: CreditCard, name: 'Accounts', path: '/accounts' },
  { icon: TrendingUp, name: 'Financial Journey', path: '/journey' },
  { icon: Lightbulb, name: 'Insights', path: '/insights' },
  { icon: Users, name: 'Family', path: '/family' },
  { icon: Settings, name: 'Settings', path: '/settings' },
];

export default function SideMenu() {
  const pathname = usePathname();
  const { isOpen, setIsOpen, toggle } = useSidebar();
  const [isMobile, setIsMobile] = useState(false);
  const [user, setUser] = useState<{ id: string, username?: string | null, email: string } | null>(null);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  
  // Update pendingPath when pathname changes (meaning navigation finished)
  useEffect(() => {
    setPendingPath(null);
  }, [pathname]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Fetch live user info
    const fetchUser = async () => {
      const data = await getCurrentUser();
      if (data) {
        setUser(data);
      }
    };
    fetchUser();

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/auth';
  };

  if (pathname === '/auth') return null;

  const initials = user?.username ? user.username.split(' ').map(n => n[0]).join('').toUpperCase() : '??';

  return (
    <>
      {/* Floating Toggle Button */}
      {/* ... previous toggle code ... */}
      {!isMobile && (
        <button 
          onClick={toggle}
          className="glass"
          style={{
            position: 'fixed',
            top: '1rem',
            left: '1rem',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 110,
            cursor: 'pointer',
            padding: 0,
            color: 'var(--accent)',
            transitionProperty: 'all',
            transitionDuration: '0.1s',
            transitionTimingFunction: 'ease',
            opacity: isOpen ? 0 : 1,
            pointerEvents: isOpen ? 'none' : 'auto',
            visibility: isOpen ? 'hidden' : 'visible',
            transitionDelay: isOpen ? '0.0s' : '0.3s',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            border: 'none'
          }}
          title={isOpen ? 'Collapse Menu' : 'Open Menu'}
        >
          <Menu size={24} />
        </button>
      )}

      {/* Backdrop for click-outside (All screens) */}
      {isOpen && (
        <div 
          onClick={() => setIsOpen(false)}
          className="sidebar-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.2)',
            backdropFilter: 'blur(2px)',
            zIndex: 90,
            cursor: 'pointer',
            transition: 'opacity 0.3s ease'
          }}
        />
      )}

      {/* Actual Sidebar */}
      <aside 
        className="glass" 
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100vh', 
          width: '280px',
          position: 'fixed',
          top: 0,
          left: isOpen ? 0 : '-300px',
          zIndex: 100,
          transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: isOpen ? '20px 0 50px rgba(0,0,0,0.5)' : 'none'
        }}
      >
        <div style={{ padding: '2rem 1.5rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <img src="/logo_v2.png" alt="Logo" style={{ width: '52px', height: '52px', objectFit: 'contain' }} />
          <span style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--accent)', flex: 1 }}>
            MyMoneyMagic
          </span>
        </div>

        <nav style={{ flex: 1, padding: '0 1rem' }}>
          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = (pendingPath || pathname) === item.path;
              const isActuallyOnPage = pathname === item.path;

              return (
                <li key={item.path}>
                  <Link 
                    href={item.path}
                    className="sidebar-link"
                    onClick={() => {
                        setPendingPath(item.path);
                        // Close sidebar on navigation for both mobile and desktop
                        setIsOpen(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '1rem',
                      borderRadius: 'var(--radius)',
                      color: isActive ? 'var(--foreground)' : 'var(--glass-60)',
                      background: isActive ? 'rgba(16, 185, 129, 0.15)' : 'transparent',
                      border: '1px solid transparent',
                      outline: 'none',
                      boxShadow: 'none',
                      transition: 'all 0.2s ease',
                      fontWeight: isActive ? 600 : 400
                    }}
                  >
                    <Icon size={20} color={isActive ? 'var(--accent)' : 'currentColor'} />
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div style={{ padding: '1.5rem' }}>
          <div 
            onClick={() => setIsLogoutModalOpen(true)}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.75rem', 
              padding: '0.75rem', 
              borderRadius: 'var(--radius)', 
              background: 'var(--glass-05)',
              cursor: 'pointer',
              border: '1px solid transparent',
              transition: 'all 0.2s'
            }}
            className="sidebar-user-card"
          >
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: 'white' }}>
              {initials}
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.username || 'Loading...'}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--glass-40)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.email || 'Securing session...'}
              </div>
            </div>
            <ChevronRight size={16} color="var(--glass-40)" />
          </div>
        </div>
      </aside>

      <ConfirmModal 
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogout}
        title="Secure Exit"
        message="Are you sure you want to log out of your vault? Your session will be securely terminated."
        confirmText="Log Out"
        type="danger"
      />
    </>
  );
}
